import React, { useMemo } from "react"
import { ButtonProps, Container, Heading } from "@chakra-ui/react"
import { SwiperOptions } from "swiper"

import { SectionProps, withSection } from "@app/hoc/Section"
import TileCarousel from "@components/TileCarousel/TileCarousel"
import useCollectionImages from "@hooks/useCollectionImages"
import { ImageProps } from "@components/Image/Image"
import FeaturedCollectionsItem from "./FeaturedCollectionsItem"

export interface Props extends SectionProps {
  title: string
  settings: GatsbyTypes.SanityCarouselSettings
  tileSize?: FeaturedTileSize
  collections: GatsbyTypes.SanityCollection[]
}

const FeaturedCollections = ({ title, settings, tileSize = "large", collections, innerRef, handleTrackingClick }: Props) => {
  const images = useCollectionImages(collections)
  const spaceBetweenTiles = SpaceBetweenTiles[tileSize]
  const tileProps = TileProps[tileSize]
  const slidesPerView = useMemo(() => {
    if (tileSize === "large") return 4.15
    if (tileSize === "medium") return 6
    return 6.15
  }, [tileSize])

  return (
    <Container
      py={[6, 4]}
      ref={innerRef}
      overflow={`hidden`}
      sx={{
        ".swiper": {
          overflow: "visible",
        },
      }}
    >
      <Heading as="h2" size="h2" textAlign="center" pb={2.5}>
        {title}
      </Heading>
      <TileCarousel
        spaceBetweenTiles={spaceBetweenTiles}
        carouselSettings={settings}
        slidesPerView={slidesPerView}
        slidesPerViewMobile={1.15}
      >
        {images?.map((item, idx) => (
          <FeaturedCollectionsItem key={idx} item={item} tileProps={tileProps} handleTrackingClick={handleTrackingClick} />
        ))}
      </TileCarousel>
    </Container>
  )
}

export interface TileSizeProps {
  imageProps: ImageProps
  buttonSize: ButtonProps["size"]
}

const SpaceBetweenTiles: Record<FeaturedTileSize, SwiperOptions["spaceBetween"]> = {
  large: 20,
  medium: 15,
  small: 10,
}

const TileProps: Record<FeaturedTileSize, TileSizeProps> = {
  large: {
    imageProps: { width: `full`, borderRadius: "xxs" },
    ratio: 267 / 334,
    buttonSize: "xl",
  },
  medium: {
    imageProps: { width: `full`, borderRadius: "xxs" },
    ratio: 205 / 250,
    buttonSize: "md",
  },
  base: {
    imageProps: { width: `full`, borderRadius: "xxs" },
    ratio: 119 / 149,
    buttonSize: "sm",
  },
}

export default React.memo(withSection(FeaturedCollections))
